'use client';

import { VideoCard } from '@/cards/VideoCard';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { AssetType } from '@vimond/services/content-discovery';
import ReactMultiCarousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

type Props = {
  assets: AssetType[];
  numElementsPerPage?: number;
  title?: string;
};

const DEFAULT_ITEMS_PER_PAGE = 4;

const CustomRightArrow = ({ ...rest }) => {
  const { onClick } = rest;
  return (
    <button
      className="group absolute -right-4 top-1/2 z-50 -translate-y-1/2 transform rounded-full bg-black p-2 opacity-50 hover:bg-white"
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
      onClick={() => onClick()}
    >
      <ArrowRightIcon className="h-6 w-6 text-white group-hover:text-black" />
    </button>
  );
};

const CustomLeftArrow = ({ ...rest }) => {
  const { onClick } = rest;
  return (
    <button
      className="left-4top-1/2 group absolute -translate-y-1/2 transform rounded-full bg-black p-2 opacity-50 hover:bg-white"
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
      onClick={() => onClick()}
    >
      <ArrowLeftIcon className="h-6 w-6 text-white group-hover:text-black" />
    </button>
  );
};

export const Slider = ({
  assets,
  numElementsPerPage = DEFAULT_ITEMS_PER_PAGE,
  title,
}: Props) => {
  const responsive = {
    lg: {
      breakpoint: { max: 3000, min: 1024 },
      items: numElementsPerPage,
      slidesToSlide: numElementsPerPage,
      partialVisibilityGutter:
        numElementsPerPage === 1 || assets.length <= numElementsPerPage
          ? 0
          : 40,
    },
    md: {
      breakpoint: { max: 1024, min: 768 },
      items: Math.min(numElementsPerPage / 2, assets.length, 3),
      slidesToSlide: Math.min(numElementsPerPage / 2, assets.length, 3),
      partialVisibilityGutter:
        assets.length <= Math.min(numElementsPerPage, 3) ? 0 : 40,
    },
    sm: {
      breakpoint: { max: 768, min: 640 },
      items: Math.min(numElementsPerPage / 2, assets.length, 2),
      slidesToSlide: Math.min(numElementsPerPage / 2, assets.length, 2),
      partialVisibilityGutter:
        assets.length <= Math.min(numElementsPerPage, 2) ? 0 : 40,
    },
    m: {
      breakpoint: { max: 639, min: 0 },
      items: 1,
      slidesToSlide: 1,
      partialVisibilityGutter: 0,
    },
  };

  return (
    <div className="relative mb-6">
      <div className="text-text-primary my-2 px-3 text-2xl font-bold sm:px-10 lg:px-14">
        {title}
      </div>

      <div className="relative h-[230px]">
        <div
          className={
            'absolute left-3 right-3 h-full sm:left-10 sm:right-10 lg:left-14 lg:right-14'
          }
        >
          <ReactMultiCarousel
            responsive={responsive}
            showDots={false}
            autoPlay={false}
            rewind
            partialVisible
            customLeftArrow={<CustomLeftArrow />}
            customRightArrow={<CustomRightArrow />}
          >
            {assets?.map((asset) => {
              return <VideoCard key={asset.id} asset={asset} />;
            })}
          </ReactMultiCarousel>
        </div>

        <div>
          <div className="from-background-primary absolute left-0 top-0 h-full w-3 bg-gradient-to-r to-transparent sm:w-10 lg:w-14"></div>
          <div className="from-background-primary absolute right-0 top-0 h-full w-3 bg-gradient-to-l to-transparent sm:w-10 lg:w-14"></div>
        </div>
      </div>
    </div>
  );
};
